import { badgeV2 } from '@synoptic/ui-kit/badge-v2/badge-v2.css.js';
import { green, white } from '@synoptic/ui-kit/palette.js';
import { responsiveStyle } from '@synoptic/ui-kit/responsive/responsive-style.js';
import { finePointerHover } from '@synoptic/ui-kit/theme-v2/fine-pointer-hover.js';
import { hairline } from '@synoptic/ui-kit/theme-v2/hairline.js';
import { themeV2 } from '@synoptic/ui-kit/theme-v2/vars.js';
import {
  borderRadius,
  fontSizes,
  fonts,
  fontWeight,
  space,
} from '@synoptic/ui-kit/tokens.js';
import { typography } from '@synoptic/ui-kit/typography.js';
import { globalStyle, style, styleVariants } from '@vanilla-extract/css';
import { pageInsetV2 } from '../_app/page-inset-v2.css.ts';

export const streamPageV2 = style([
  pageInsetV2,
  {
    display: 'flex',
    flexDirection: 'column',
    gap: space.s48,
    minWidth: 0,
  },
]);

export const streamPageSectionV2 = style({
  display: 'flex',
  flexDirection: 'column',
  gap: space.s20,
  minWidth: 0,
});

export const streamPageBackV2 = style({
  ...typography.titleLg,
  fontFamily: fonts.sans,
  fontWeight: fontWeight.medium,
  letterSpacing: '-0.03em',
  display: 'inline-flex',
  alignItems: 'center',
  gap: space.s16,
  width: 'fit-content',
  padding: 0,
  border: 0,
  background: 'none',
  color: themeV2.color.text.primary,
  cursor: 'pointer',
});

export const streamPageHeaderGradientV2 = {
  data: 'linear-gradient(105deg, rgba(64, 58, 86, 0.70) 4.95%, rgba(23, 24, 27, 0.70) 96.63%)',
  custom:
    'linear-gradient(105deg, rgba(71, 61, 32, 0.70) 4.95%, rgba(23, 24, 27, 0.70) 96.63%)',
  package:
    'linear-gradient(105deg, rgba(46, 70, 92, 0.70) 4.95%, rgba(23, 24, 27, 0.70) 96.63%)',
} as const;

export const streamBannerBorderV2 = `inset 0 0 0 2px ${themeV2.color.border.subtle}`;

export const streamPageHeaderWashV2 = styleVariants(
  streamPageHeaderGradientV2,
  (backgroundImage) => ({
    backgroundImage,
  }),
);

export const streamPageHeaderV2 = style({
  display: 'flex',
  flexDirection: 'column',
  gap: space.s24,
  width: '100%',
  minWidth: 0,
  minHeight: 313,
  padding: space.s24,
  borderRadius: borderRadius.r16,
  boxShadow: streamBannerBorderV2,
});

export const streamPageHeaderMainV2 = style({
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: space.s16,
  minWidth: 0,
});

export const streamPageHeaderTopV2 = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: space.s16,
  flexWrap: 'wrap',
  minWidth: 0,
});

export const streamPageHeaderIdentityV2 = style({
  display: 'flex',
  alignItems: 'center',
  gap: space.s16,
  flexWrap: 'wrap',
  minWidth: 0,
});

export const streamPageLiveV2 = style([
  badgeV2,
  {
    height: 32,
    gap: 6,
    backgroundColor: `color-mix(in srgb, ${green[400]} 10%, transparent)`,
    boxShadow: hairline(`color-mix(in srgb, ${green[400]} 5%, transparent)`),
    color: green[400],
    '::before': {
      content: '',
      width: 8,
      height: 8,
      borderRadius: borderRadius.r64,
      backgroundColor: green[400],
    },
  },
]);

export const streamPageHostV2 = style({
  display: 'inline-flex',
  alignItems: 'center',
  gap: space.s8,
  minWidth: 0,
  fontFamily: fonts.mono,
  fontWeight: fontWeight.medium,
  fontSize: fontSizes.bodySm,
  lineHeight: 1.4,
  letterSpacing: '-0.04em',
  color: themeV2.color.text.tertiary,
});

export const streamPageActionsV2 = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  gap: space.s16,
  marginLeft: 'auto',
});

export const streamPageTitleV2 = style({
  margin: 0,
  fontWeight: fontWeight.medium,
  fontSize: fontSizes.titleLg,
  lineHeight: 1.3,
  letterSpacing: '-0.03em',
  color: themeV2.color.text.primary,
  ...responsiveStyle({
    tablet: {
      fontSize: fontSizes.headlineMd,
    },
  }),
});

export const streamPageDescriptionV2 = style({
  display: 'flex',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  gap: space.s4,
  maxWidth: '100%',
  margin: 0,
  ...typography.bodyMd,
  color: themeV2.color.text.quaternary,
});

export const streamPageDescriptionClampV2 = style({
  flexWrap: 'nowrap',
});

globalStyle(`${streamPageDescriptionClampV2} > span`, {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
});

export const streamPageShowMoreV2 = style({
  ...typography.bodyMd,
  flexShrink: 0,
  margin: 0,
  padding: 0,
  border: 0,
  background: 'none',
  color: themeV2.color.text.quaternary,
  textDecoration: 'underline',
  cursor: 'pointer',
});

export const customStreamExplainerV2 = style({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: space.s16,
  width: '100%',
  padding: space.s16,
  borderRadius: borderRadius.r16,
  boxShadow: hairline(white[50]),
});

export const customStreamExplainerCopyV2 = style({
  display: 'flex',
  flexDirection: 'column',
  gap: space.s8,
  minWidth: 0,
});

export const customStreamExplainerTitleV2 = style({
  ...typography.labelLg,
  margin: 0,
  letterSpacing: '-0.03em',
  color: themeV2.color.text.primary,
});

export const customStreamExplainerBodyV2 = style({
  ...typography.labelMd,
  margin: 0,
  color: themeV2.color.text.quaternary,
});

export const customStreamTargetsV2 = style({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: space.s16,
  width: '100%',
  minWidth: 0,
  padding: space.s16,
  borderRadius: borderRadius.r16,
  boxShadow: hairline(white[50]),
});

export const customStreamTargetsCopyV2 = style({
  display: 'flex',
  flexDirection: 'column',
  gap: space.s8,
  minWidth: 0,
});

export const customStreamTargetsLabelV2 = style({
  margin: 0,
  fontFamily: fonts.mono,
  fontWeight: fontWeight.medium,
  fontSize: fontSizes.bodySm,
  lineHeight: 1.3,
  letterSpacing: '-0.04em',
  color: themeV2.color.text.quaternary,
});

export const customStreamTargetsChipsV2 = style({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: space.s4,
  margin: 0,
  padding: 0,
  listStyle: 'none',
  minWidth: 0,
});

export const customStreamTargetsMoreV2 = style({
  ...typography.labelSm,
  margin: 0,
  color: themeV2.color.text.quaternary,
  letterSpacing: '-0.04em',
  whiteSpace: 'nowrap',
  flexShrink: 0,
});

export const customStreamTargetChipV2 = style({
  ...typography.labelSm,
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 24,
  padding: `${space.s4}px ${space.s8}px`,
  borderRadius: borderRadius.r8,
  boxShadow: hairline(themeV2.color.border.subtle),
  color: themeV2.color.text.primary,
  letterSpacing: '-0.04em',
});

export const customStreamTargetsActionV2 = style({
  flexShrink: 0,
});

export const streamPageTagV2 = style({
  height: 32,
  backgroundColor: white[50],
  boxShadow: hairline(white[50]),
  color: white[800],
});

export const streamPageTagsV2 = style({
  display: 'flex',
  marginTop: 'auto',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: space.s16,
  width: '100%',
  minWidth: 0,
});

export const streamPageFollowersV2 = style({
  marginLeft: 'auto',
});

export const streamPageTabsV2 = style({
  display: 'flex',
  alignItems: 'flex-end',
  gap: space.s4,
  minWidth: 0,
  overflowX: 'auto',
  scrollbarWidth: 'none',
  borderBottom: `1px solid ${white[50]}`,
});

export const streamPageTabV2 = style({
  ...typography.labelLg,
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: space.s8,
  minWidth: 80,
  padding: `0 ${space.s4}px`,
  color: white[400],
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  '::after': {
    content: '',
    width: '100%',
    minWidth: 72,
    height: 2,
    borderRadius: borderRadius.r4,
    backgroundColor: 'transparent',
  },
  '@media': {
    [finePointerHover]: {
      selectors: {
        '&:hover:not([aria-current="page"])': {
          color: themeV2.color.text.primary,
        },
      },
    },
  },
  selectors: {
    '&[aria-current="page"]': {
      color: themeV2.color.text.primary,
    },
    '&[aria-current="page"]::after': {
      backgroundColor: themeV2.color.text.primary,
    },
    '&:focus-visible': {
      outline: `2px solid ${themeV2.color.border.focus}`,
      outlineOffset: '-1px',
    },
  },
});
